import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { useLingui } from "@lingui/react/macro";
import { Button } from "./button";
import { XIcon } from "lucide-react";

const badgeVariants = cva(
  "inline-flex items-center justify-center border border-transparent font-medium w-fit whitespace-nowrap shrink-0 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        yellow:
          "border-yellow-50 bg-yellow-50 [&_svg]:text-yellow-800 [a&]:hover:bg-yellow-500/90",
        purple:
          "border-purple-50 bg-purple-50 [&_svg]:text-purple-600 [a&]:hover:bg-purple-500/90",
        red: "border-red-50 bg-red-50 [&_svg]:text-red-700 [a&]:hover:bg-red-500/90",
        green:
          "border-green-50 bg-green-50 [&_svg]:text-green-700 [a&]:hover:bg-green-500/90",
        blue: "border-blue-50 bg-blue-50 [&_svg]:text-blue-700 [a&]:hover:bg-blue-500/90",
        cyan: "border-cyan-200 bg-cyan-50 text-cyan-700 [a&]:hover:bg-cyan-500/90",
        fuchsia:
          "border-fuchsia-50 bg-fuchsia-50 [&_svg]:text-fuchsia-700 [a&]:hover:bg-fuchsia-500/90",
        secondary:
          "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "text-primary underline-offset-4 [a&]:hover:underline",
      },
      size: {
        default: "px-2 py-0.5 text-xs [&>svg]:size-3",
        lg: "text-sm h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 [&>svg]:size-4 ",
      },
      shape: {
        round: "rounded-full",
        square: "rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      shape: "round",
    },
    compoundVariants: [
      {
        shape: "square",
        size: "lg",
        className: "rounded-md",
      },
      {
        shape: "square",
        size: "default",
        className: "rounded-sm",
      },
    ],
  }
);

export type BadgeProps = React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
    onDelete?: () => void;
  };

function Badge({
  className,
  variant = "default",
  size = "default",
  shape = "round",
  asChild = false,
  children,
  onDelete,
  onClick,
  onDoubleClick,
  onMouseDown,
  ...props
}: BadgeProps) {
  const { t } = useLingui();

  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      data-size={size}
      data-shape={shape}
      className={cn(badgeVariants({ variant, size, shape }), className)}
      {...(onClick ? props : { onDoubleClick, onMouseDown, ...props })}
    >
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          onDoubleClick={onDoubleClick}
          onMouseDown={onMouseDown}
          className="badge-inner"
        >
          {children}
        </button>
      ) : (
        children
      )}
      {onDelete && (
        <Button
          aria-label={t`Ta bort`}
          title={t`Ta bort`}
          size="icon-xs"
          variant="ghost"
          onClick={onDelete}
          className="badge-delete transition-none w-min"
        >
          <XIcon />
        </Button>
      )}
    </Comp>
  );
}

export { Badge, badgeVariants };
